import {
    REQUIRED_EMAIL_VERIFICATION,
    REQUIRED_PHONE_VERIFICATION,
    REQUEST_RECIPIENT_DOWNLOAD_APPROVAL,
    VIEWER_ONLY,
    PROTECT_ON_DOWNLOAD,
    PROTECT_AND_TRACK_DUMMY,
    PREVENT_DOWNLOAD_UNPROTECTED,
    APPLY_WATERMARK,
    DATA_RETENTION,
    DAYS_RETENTION_VALUE,
    LANGUAGE_POLICY,
    LIMIT_TO_USERS_VALUE,
    PASSWORD_PROTECTION,
    REQUIRED_PASSWORD_VERIFICATION,
} from '@/consts';

export const FORWARDING_OPTIONS = 'forwardingOptions';

/**
 * Defines how policy settings are grouped semantically in the UI
 * Each group has a title and a list of setting IDs that belong to it
 */
export interface PolicySettingsSubgroup {
    titleKey: string;
    descriptionKey?: string;
    settingIds: string[];
    linkConfig?: {
        textKey: string;
        action: string;
    };
}

/**
 * Maps policy settings groups to their semantic subgroups
 * This defines the visual organization in the new card-based UI
 */
export const POLICY_SETTINGS_SUBGROUPS: Record<string, PolicySettingsSubgroup[]> = {
    access: [
        // Policy Configuration subgroups
        {
            titleKey: 'policyEditor.access.subgroups.policyConfiguration',
            descriptionKey: 'policyEditor.access.subgroups.policyConfigurationDesc',
            settingIds: ['limitToUsers', LIMIT_TO_USERS_VALUE],
        },
        // Access Control subgroups
        {
            titleKey: 'policyEditor.access.subgroups.recipientSharing',
            descriptionKey: 'policyEditor.access.subgroups.recipientSharingDesc',
            settingIds: [FORWARDING_OPTIONS],
        },
        {
            titleKey: 'policyEditor.access.subgroups.verification',
            descriptionKey: 'policyEditor.access.subgroups.verificationDesc',
            settingIds: [
                REQUIRED_EMAIL_VERIFICATION,
                REQUIRED_PHONE_VERIFICATION,
                REQUIRED_PASSWORD_VERIFICATION,
            ],
        },
        {
            titleKey: 'policyEditor.access.subgroups.allowingAccess',
            descriptionKey: 'policyEditor.access.subgroups.allowingAccessDesc',
            settingIds: [
                REQUEST_RECIPIENT_DOWNLOAD_APPROVAL,
            ],
        },
        // Data Protection subgroups (all settings are in access group from API)
        {
            titleKey: 'policyEditor.flow.subgroups.cloudApplications',
            descriptionKey: 'policyEditor.flow.subgroups.cloudApplicationsDesc',
            settingIds: [PROTECT_AND_TRACK_DUMMY], // Dummy toggle with no API mapping
        },
        {
            titleKey: 'policyEditor.flow.subgroups.downloadingFiles',
            descriptionKey: 'policyEditor.flow.subgroups.downloadingFilesDesc',
            settingIds: [
                VIEWER_ONLY,
                PASSWORD_PROTECTION,
                PROTECT_ON_DOWNLOAD, // Encrypt using Rights Management
                PREVENT_DOWNLOAD_UNPROTECTED,
            ],
        },
        {
            titleKey: 'policyEditor.flow.subgroups.watermarking',
            descriptionKey: 'policyEditor.flow.subgroups.watermarkingDesc',
            settingIds: [APPLY_WATERMARK],
        },
        {
            titleKey: 'policyEditor.flow.subgroups.dataExpiration',
            descriptionKey: 'policyEditor.flow.subgroups.dataExpirationDesc',
            settingIds: [DATA_RETENTION, DAYS_RETENTION_VALUE, 'dataRetention.AutoDelete'],
        },
        // Recipient Experience subgroups
        {
            titleKey: 'policyEditor.permissions.subgroups.recipientLanguage',
            descriptionKey: 'policyEditor.permissions.subgroups.recipientLanguageDesc',
            settingIds: [LANGUAGE_POLICY],
        },
    ],
    flow: [
        // Kept for backward compatibility
    ],
    permissions: [
        // Kept for backward compatibility
    ],
};

/**
 * Helper to determine which subgroup a setting belongs to
 */
export const findSettingSubgroup = (
    settingsGroupType: string,
    settingId: string,
): PolicySettingsSubgroup | null => {
    const subgroups = POLICY_SETTINGS_SUBGROUPS[settingsGroupType];
    if (!subgroups) {
        return null;
    }

    return (
        subgroups.find((subgroup) => subgroup.settingIds.includes(settingId)) || null
    );
};
